import { useState } from 'react';

import { MessageSquarePlus } from 'lucide-react';

import type { ScreeningKind } from '@/api/schema';
import { ApiError } from '@/api/client';
import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Select';
import { FLYST_LABEL, RSR_LABEL } from '@/features/candidates/labels';
import { formatDateTime, type DateFormat } from '@/lib/dates';

import { useAddScreeningNote, useScreeningNotes } from '../api';
import styles from './ScreeningNotes.module.css';

/** How long ago, in the words people use. Exact time is a hover away. */
function ago(iso: string, now: Date): string {
  const minutes = Math.round((now.getTime() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}

/** The stage a note was written at, in the vocabulary of its own screening. */
function stageLabel(kind: ScreeningKind, stored: string): string {
  const labels: Record<string, string | undefined> = kind === 'flyst' ? FLYST_LABEL : RSR_LABEL;
  return labels[stored] ?? stored;
}

/**
 * The notes on one screening.
 *
 * Composer at the top, timeline below, newest first. A note written a moment ago is
 * the one being looked for, and putting it at the bottom of a long list means scrolling
 * past everything to find it.
 *
 * Each note carries the stage it was written at, not the stage the candidate is in now.
 * "Waiting on the CV" is true of Communication and reads as nonsense against Sponsor
 * screening arranged three weeks later.
 */
export function ScreeningNotes({
  candidateId,
  kind,
  dateFormat,
}: {
  candidateId: string;
  kind: ScreeningKind;
  dateFormat: DateFormat;
}) {
  const notes = useScreeningNotes(candidateId, kind);
  const add = useAddScreeningNote(candidateId, kind);

  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const now = new Date();

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    setProblem(null);
    try {
      await add.mutateAsync(body);
      setDraft('');
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'That note was not saved.');
    }
  };

  return (
    <section className={styles.notes}>
      <h3 className={styles.heading}>Notes</h3>

      <div className={styles.composer}>
        <Textarea
          rows={3}
          value={draft}
          aria-label="Write a note"
          placeholder="What happened?"
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
        <Button
          size="sm"
          icon={<MessageSquarePlus size={15} />}
          loading={add.isPending}
          disabled={!draft.trim()}
          onClick={() => {
            void submit();
          }}
        >
          Add note
        </Button>
      </div>

      {problem ? (
        <Alert tone="danger">
          <b>{problem}</b>
        </Alert>
      ) : null}

      {notes.isPending ? (
        <p className={styles.empty}>
          <Spinner size={14} label="Loading the notes" />
        </p>
      ) : notes.isError ? (
        <p className={styles.empty}>Those notes did not load.</p>
      ) : notes.data.length === 0 ? (
        <p className={styles.empty}>Nothing written yet.</p>
      ) : (
        <ol className={styles.timeline}>
          {notes.data.map((note) => (
            <li key={note.id} className={styles.note}>
              <p className={styles.body}>{note.body}</p>
              <p className={styles.meta}>
                <span>{note.author?.full_name ?? 'Someone since removed'}</span>
                <span aria-hidden="true">·</span>
                <span title={formatDateTime(note.created_at, dateFormat)}>
                  {ago(note.created_at, now)}
                </span>
                <span aria-hidden="true">·</span>
                {/* Where they stood then, not where they are now. */}
                <span className={styles.stage}>{stageLabel(kind, note.stage_at_time)}</span>
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
